/**
 * Planes Back office para administradoras: contenido y cálculo de la cotización.
 *
 * Lo consumen el Cotizador Back office del superadmin
 * (screens/comercial/CotizadorBackoffice.jsx) y el PDF
 * (components/presupuesto/CotizacionBackofficeTemplate.jsx).
 *
 * Fuente del contenido: el deck de octubre de 2026
 * (propuestas-administradoras/habitia-administradoras.html, slides 03 a 07,
 * 11 y 12). Los textos van TAL CUAL el deck: si cambia el deck, se cambia acá.
 *
 * Precios: por unidad, por mes y siempre "+ IVA". Para la administradora el IVA
 * no es un costo, así que acá no se calcula ni se muestra (ni montos con IVA ni
 * el valor del IVA).
 *
 * Helpers PUROS y sin dependencias: no importar i18n ni utils/formatters (usan
 * require.context y rompen jest).
 */

/**
 * @typedef {"plataforma" | "backoffice" | "full"} PlanKey
 */

/**
 * Valor de una celda de la matriz "Quién hace cada cosa".
 * @typedef {"vos" | "habitia" | "incluido" | "extra" | "no"} ValorMatriz
 */

/**
 * @typedef {Object} PlanBO
 * @property {PlanKey} key
 * @property {string} nombre — "Plataforma" | "Back office" | "Full Back office"
 * @property {number} nivel — 1 a 3 (barras de nivel)
 * @property {string} color — color del plan en el PDF
 * @property {string} kicker
 * @property {string} resumen — tarjeta del plan (slide 03)
 * @property {string} frase — bajada de la ficha del plan (slides 04 a 06)
 * @property {number} precioDesde — $U por unidad y por mes, + IVA (precio de lista)
 * @property {{ quien: string, que: string }[]} roles
 * @property {string} incluyeTitulo
 * @property {string[]} incluye
 * @property {string|null} incluyeAnterior
 * @property {string|null} sigueConVosTitulo
 * @property {string|null} sigueConVos
 * @property {string} hoy
 * @property {string} conHabitia
 */

/**
 * @typedef {Object} FilaMatrizBO
 * @property {string} key
 * @property {string} label
 * @property {ValorMatriz} plataforma
 * @property {ValorMatriz} backoffice
 * @property {ValorMatriz} full
 */

/**
 * @typedef {Object} ExtraCotizado
 * @property {string} key
 * @property {string} label
 * @property {number} monto — $U por mes, + IVA
 */

/**
 * Salida de cotizarBackoffice. Todos los montos son $U por mes, + IVA.
 * @typedef {Object} CotizacionBO
 * @property {boolean} valido — plan conocido y unidades > 0
 * @property {PlanKey|null} plan — null si el plan no existe
 * @property {string} planNombre
 * @property {number} unidades
 * @property {number} edificios
 * @property {number} precioLista — precioDesde del plan
 * @property {number} precioUnidad — el cargado o, si no hay, el de lista
 * @property {number} subtotalUnidades
 * @property {ExtraCotizado[]} extras — solo los activos, con monto y disponibles en el plan
 * @property {number} extrasTotal
 * @property {number} totalCalculado
 * @property {number} totalFinal — el ajustado a mano o, si no hay, el calculado
 * @property {boolean} ajustado
 * @property {number} ajusteMonto — totalFinal − totalCalculado
 * @property {number} ajustePct — % sobre el total calculado
 * @property {number} precioUnidadFinal — (totalFinal − extras) / unidades
 * @property {boolean} precioUnidadExacto — precioUnidadFinal × unidades + extras da
 *   justo el totalFinal (con un total ajustado a mano puede no cerrar al centésimo)
 * @property {boolean} debajoDelPiso — precioUnidadFinal por debajo del de lista
 */

// ─── Helpers internos ──────────────────────────────────────────────────────

// Congela en profundidad: los catálogos son la fuente única del contenido y no
// se mutan (copiar antes de ordenar o editar).
function congelar(valor) {
  if (valor && typeof valor === "object" && !Object.isFrozen(valor)) {
    Object.values(valor).forEach(congelar);
    Object.freeze(valor);
  }
  return valor;
}

// Número opcional: acepta number o string numérico. Vacío, NaN o cualquier otra
// cosa → null (así un input vacío no se lee como 0).
function numeroOpcional(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

// Cantidad entera >= 0 (unidades, edificios): trunca los decimales.
function enteroNoNegativo(v) {
  const n = numeroOpcional(v);
  return n !== null && n > 0 ? Math.floor(n) : 0;
}

// Días de validez: entero >= 0; si no viene o no sirve, el default.
function diasValidez(v) {
  const n = numeroOpcional(v);
  return n !== null && n >= 0 ? Math.floor(n) : VALIDEZ_DIAS_DEFAULT;
}

// Redondeo a centésimos en base decimal (1.005 → 1.01, no 1.00 por la
// representación binaria). No finito → 0; nunca devuelve -0.
function round2(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  const abs = Math.abs(v);
  const txt = String(abs);
  const r = txt.includes("e")
    ? Math.round(abs * 100) / 100
    : Math.round(Number(`${txt}e2`)) / 100;
  return (v < 0 ? -r : r) || 0;
}

// Separador de miles uruguayo sobre un entero: 1234567 → "1.234.567".
function miles(n) {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

// Normaliza fechas: Date, ISO, "AAAA-MM-DD" (al mediodía local, para que no se
// corra un día en UTC-3), epoch en ms o Timestamp de Firestore. Mismo criterio
// que utils/dates.toJSDate, sin depender de firebase. Inválida → null.
function aFecha(v) {
  let d = null;
  if (v instanceof Date) d = v;
  else if (typeof v === "string" && v.trim()) {
    const s = v.trim();
    d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T12:00:00` : s);
  } else if (typeof v === "number") d = new Date(v);
  else if (v && typeof v.toDate === "function") d = v.toDate();
  else if (v && typeof v === "object" && Number.isFinite(v.seconds)) d = new Date(v.seconds * 1000);
  return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
}

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "setiembre", "octubre", "noviembre", "diciembre"];
const MESES_CORTOS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "set", "oct", "nov", "dic"];

// ¿El precio por unidad final reproduce el total? Con el total ajustado a mano
// puede no cerrar al centésimo: 62.000 / 340 = 182,35, pero 182,35 × 340 =
// 61.999. Sin unidades no hay precio por unidad que cerrar.
function cierraPorUnidad(unidades, precioUnidadFinal, extrasTotal, totalFinal) {
  if (!(unidades > 0)) return true;
  return Math.abs(round2(precioUnidadFinal * unidades + extrasTotal) - round2(totalFinal)) < 0.005;
}

// "12 edificios · 340 unidades" (sin edificios: solo las unidades).
function carteraTexto(edificios, unidades) {
  const plural = (n, uno, varios) => `${miles(n)} ${n === 1 ? uno : varios}`;
  const u = plural(unidades, "unidad", "unidades");
  return edificios > 0 ? `${plural(edificios, "edificio", "edificios")} · ${u}` : u;
}

// ─── Constantes ────────────────────────────────────────────────────────────

/** Orden de los planes, de menos a más delegación. */
export const PLAN_KEYS = congelar(["plataforma", "backoffice", "full"]);
export const PLAN_DEFAULT = "backoffice";
export const VALIDEZ_DIAS_DEFAULT = 30;
/** Leyenda que acompaña a todo monto: nunca se muestran montos con IVA. */
export const IVA_LEYENDA = "+ IVA";

/**
 * Los tres planes, con los textos del deck (slides 03 a 06).
 * @type {Readonly<Record<PlanKey, PlanBO>>}
 */
export const PLANES_BO = congelar({
  plataforma: {
    key: "plataforma",
    nombre: "Plataforma",
    nivel: 1,
    color: "#0080FF",
    kicker: "Plan 1 · Opera tu equipo",
    resumen: "La herramienta completa con IA. Tu equipo la usa todos los días; nosotros damos soporte y capacitación.",
    frase: "Tu equipo opera con una plataforma moderna, con IA en cada proceso.",
    precioDesde: 90,
    roles: [
      { quien: "Tu equipo", que: "Opera la plataforma todos los días" },
      { quien: "Habitia", que: "Soporte, capacitación y migración sin costo" },
      { quien: "Habi", que: "Responde por escrito a tus residentes; lo que no resuelve te llega a vos" },
    ],
    incluyeTitulo: "Qué incluye",
    incluye: [
      "Facturas y comprobantes leídos por IA",
      "Cierre de mes con prorrateo, fondos y mora calculados",
      "Registro de cobranzas, multas y cargos puntuales",
      "Recordatorios de vencimiento y avisos de morosidad automáticos",
      "Estados de cuenta e informe de gastos para la comisión",
      "Gastos de vecinos e ingresos del edificio",
      "App web del residente con su estado de cuenta",
      "Gestión de residentes y consultas que Habi deriva",
      "Incidencias, proveedores, mantenimientos y amenities",
      "Novedades, citaciones a asamblea y documentos",
      "Personal: asistencia, tareas, turnos y licencias",
      "Chat con el personal del edificio",
    ],
    incluyeAnterior: null,
    sigueConVosTitulo: null,
    sigueConVos: null,
    hoy: "Cargás cada factura y armás el prorrateo a mano; un dato mal cargado obliga a rehacer el cierre.",
    conHabitia: "La IA extrae los datos de cada factura y el cierre sale con prorrateo, fondos y mora ya calculados. Tu equipo revisa y publica.",
  },
  backoffice: {
    key: "backoffice",
    nombre: "Back office",
    nivel: 2,
    color: "#0066CC",
    kicker: "Plan 2 · Habitia ejecuta",
    resumen: "Habitia ejecuta los módulos principales: finanzas, operaciones, comunicación y personal. Vos aprobás y decidís.",
    frase: "Delegás la operación del día a día y te quedás con los números y las decisiones.",
    precioDesde: 150,
    roles: [
      { quien: "Habitia", que: "Ejecuta finanzas, operaciones, comunicación y personal" },
      { quien: "Vos", que: "Aprobás el cierre y pagás desde tu banco" },
      { quien: "Habi", que: "Responde por escrito y deriva lo que necesita una persona" },
    ],
    incluyeTitulo: "Lo hace Habitia",
    incluye: [
      "Carga de facturas y comprobantes de pago",
      "Multas, cargos puntuales, gastos de vecinos e ingresos",
      "Registro de cobranzas y avisos de morosidad",
      "Estados de cuenta, informe de gastos y cierre de mes",
      "Incidencias con el proveedor, hasta cerrarlas",
      "Proveedores, mantenimientos, amenities y documentos",
      "Gestión de residentes y consultas que Habi deriva",
      "Novedades y citaciones a asamblea",
      "Personal: asistencia, tareas, turnos, licencias y chat",
      "Trámites online del edificio: BPS, DGI, BSE, MTSS, UTE, OSE, Antel",
      "Gestión de la póliza de seguros del edificio",
      "Informe mensual con rendición y presupuesto anual",
    ],
    incluyeAnterior: "Incluye todo lo de Plataforma.",
    sigueConVosTitulo: "Sigue con vos",
    sigueConVos: "Pagar las facturas desde tu banco (nosotros registramos el pago), liquidar los sueldos y los aportes al BPS del personal, aprobar el cierre y cada decisión.",
    hoy: "Cada consulta y reclamo entra por tu teléfono y depende de tu disponibilidad.",
    conHabitia: "Habi responde mensajes a cualquier hora y nuestro equipo, en horario de oficina, sigue cada incidencia hasta cerrarla. Lo sensible te llega a vos.",
  },
  full: {
    key: "full",
    nombre: "Full Back office",
    nivel: 3,
    color: "#0052A3",
    kicker: "Plan 3 · Habitia gestiona todo",
    resumen: "Habitia gestiona todo, incluidos el pago de facturas en el banco, la cobranza por pasarela de pago, la conciliación bancaria, las actas y los sueldos.",
    frase: "Delegás todo el back office; vos seguís al frente.",
    precioDesde: 200,
    roles: [
      { quien: "Habitia", que: "Gestiona todo, también pagos, cobranza, conciliación y sueldos" },
      { quien: "Vos", que: "Autorizás los pagos y aprobás cada cierre" },
      { quien: "Habi", que: "Atiende por escrito y por llamada de WhatsApp" },
    ],
    incluyeTitulo: "Qué suma sobre Back office",
    incluye: [
      "Pago de facturas en el banco, con tu autorización",
      "Cobranza por pasarela de pago y otros medios",
      "Conciliación bancaria mensual",
      "Contabilidad del edificio",
      "Liquidación de sueldos y aportes al BPS del personal",
      "Seguimiento de obras y proyectos",
      "Actas de asamblea, desde el audio o con plantilla",
      "Gestión de morosos: intimaciones y carpeta legal",
      "Habi por llamada de WhatsApp, en español",
      "Ejecutivo de cuenta con WhatsApp directo",
    ],
    incluyeAnterior: "Incluye todo lo de Back office.",
    sigueConVosTitulo: "Siempre con vos",
    sigueConVos: "La conducción de las asambleas, la comisión, las visitas al edificio y cada decisión sobre cierres, pagos y obras.",
    hoy: "Coordinás cierre, cobranza, pagos, sueldos y conciliación entre distintos sistemas y personas.",
    conHabitia: "Nuestro equipo opera el ciclo completo sobre la plataforma. Vos autorizás los pagos y aprobás cada cierre.",
  },
});

/** Franja "En todos los planes" (slide 03). */
export const EN_TODOS_LOS_PLANES = "La plataforma completa y Habi por escrito. Quedan con vos la conducción de las asambleas, la comisión, las visitas al edificio y cada decisión: ningún plan incluye tareas presenciales.";

const fila = (key, label, plataforma, backoffice, full) => ({ key, label, plataforma, backoffice, full });

/**
 * Matriz "Quién hace cada cosa" (slide 07): 7 grupos, 19 filas.
 * La fila habi/habiLlamada comparte key y label con EXTRAS_BO.habiLlamada: si la
 * cotización lleva ese extra activo, el PDF la marca incluida en el plan elegido.
 * @type {ReadonlyArray<{ key: string, label: string, filas: FilaMatrizBO[] }>}
 */
export const MATRIZ_BO = congelar([
  {
    key: "finanzas",
    label: "Finanzas",
    filas: [
      fila("cargaFacturas", "Carga de facturas y registro de pagos con su comprobante", "vos", "habitia", "habitia"),
      fila("multasCargos", "Multas, cargos puntuales, gastos de vecinos e ingresos del edificio", "vos", "habitia", "habitia"),
      fila("cobranzasMorosidad", "Registro de cobranzas y avisos automáticos de morosidad", "vos", "habitia", "habitia"),
      fila("estadosCierre", "Estados de cuenta, informe de gastos y cierre de mes", "vos", "habitia", "habitia"),
      fila("conciliacion", "Conciliación bancaria", "no", "no", "habitia"),
    ],
  },
  {
    key: "banco",
    label: "Banco",
    filas: [
      fila("pagoFacturas", "Pago de facturas en el banco", "no", "no", "habitia"),
      fila("pasarela", "Cobranza por pasarela de pago y otros medios", "no", "no", "habitia"),
    ],
  },
  {
    key: "operaciones",
    label: "Operaciones",
    filas: [
      fila("incidencias", "Incidencias, proveedores y mantenimientos", "vos", "habitia", "habitia"),
      fila("amenitiesResidentes", "Amenities, documentos y gestión de residentes", "vos", "habitia", "habitia"),
      fila("obras", "Obras y proyectos", "no", "no", "habitia"),
    ],
  },
  {
    key: "comunicaciones",
    label: "Comunicaciones",
    filas: [
      fila("novedades", "Novedades y citaciones a asamblea", "vos", "habitia", "habitia"),
      fila("consultasDerivadas", "Consultas que Habi deriva a una persona", "vos", "habitia", "habitia"),
      fila("actas", "Actas de asamblea, desde el audio o con plantilla", "no", "no", "habitia"),
    ],
  },
  {
    key: "personal",
    label: "Personal",
    filas: [
      fila("personal", "Asistencia, tareas, turnos, licencias y chat con el personal", "vos", "habitia", "habitia"),
      fila("sueldos", "Liquidación de sueldos y aportes al BPS", "no", "no", "habitia"),
    ],
  },
  {
    key: "habi",
    label: "Habi",
    filas: [
      fila("habiEscrito", "Habi por escrito: WhatsApp 24/7, en español, inglés y portugués", "incluido", "incluido", "incluido"),
      fila("habiLlamada", "Habi por llamada de WhatsApp, en español", "extra", "extra", "incluido"),
    ],
  },
  {
    key: "gestionAdministrativa",
    label: "Gestión administrativa",
    filas: [
      fila("tramites", "Trámites online del edificio, póliza de seguros y presupuesto anual", "no", "habitia", "habitia"),
      fila("contabilidad", "Contabilidad, intimaciones y ejecutivo de cuenta", "no", "no", "habitia"),
    ],
  },
]);

/** Leyenda de la matriz (slide 07), en el orden del deck. */
export const LEYENDA_BO = congelar([
  { valor: "vos", etiqueta: "Vos", significado: "la herramienta está incluida y la opera tu equipo" },
  { valor: "habitia", etiqueta: "Habitia", significado: "lo ejecuta nuestro equipo" },
  { valor: "incluido", etiqueta: "Incluido", significado: "viene con el plan" },
  { valor: "extra", etiqueta: "Extra a cotizar", significado: "con costo adicional" },
  { valor: "no", etiqueta: "—", significado: "no incluido" },
]);

export const NOTA_MATRIZ_BO = "Habi por llamada: incluido en Full Back office · extra a cotizar en Plataforma y Back office (se activa por edificio). En Plataforma y Back office las facturas se pagan desde tu banco y el pago se registra en Habitia.";
export const PIE_MATRIZ_BO = "Cada plan incluye la plataforma completa · nada presencial en ningún plan";

/** Condiciones comerciales (chips de la propuesta económica). */
export const CONDICIONES_BO = congelar([
  { titulo: "Por unidad, sin mínimos", texto: "Se paga por unidad, por mes. Sin mínimos ni cargos fijos." },
  { titulo: "Sin permanencia", texto: "Seguís porque te sirve, no por un plazo." },
  { titulo: "Migración sin costo", texto: "Pasamos tu cartera por tandas, sin interrumpir ningún cierre y con todo tu historial." },
  { titulo: "Nada presencial", texto: "Ningún plan incluye tareas presenciales: las visitas al edificio, la presencia en las asambleas y los trámites en ventanilla quedan con vos." },
]);

/** "Cómo empezamos" (slide 11, sin la reunión: la propuesta ya es ese paso). */
export const COMO_EMPEZAMOS_BO = congelar([
  { n: 1, titulo: "Piloto", sub: "2 o 3 edificios", texto: "Empezás con pocos edificios, que se pagan como cualquier edificio. Sin permanencia: seguís si te convence." },
  { n: 2, titulo: "Migración", sub: "El resto, por tandas", texto: "Pasamos tu cartera sin costo, sin interrumpir ningún cierre y con todo tu historial." },
  { n: 3, titulo: "Acompañamiento", sub: "Soporte y capacitación", texto: "Capacitamos a tu equipo y te damos soporte en horario de oficina. Habi responde mensajes a cualquier hora." },
]);

/** Contacto (slide 12) y datos legales de Habitia. */
export const CONTACTO_BO = congelar({
  nombre: "Federico Casas",
  email: "info@habitia.ai",
  whatsapp: "099 761 810",
  web: "habitia.ai",
  razonSocial: "Habitia S.A.S.",
  rut: "220857790010",
  ciudad: "Montevideo, Uruguay",
  horario: "Lunes a viernes, en horario de oficina",
});

/**
 * Extras que se cotizan aparte (monto mensual, + IVA). Solo aplican en los
 * planes de `disponibleEn`; en Full, Habi por llamada ya viene incluido.
 */
export const EXTRAS_BO = congelar({
  habiLlamada: {
    key: "habiLlamada",
    label: "Habi por llamada de WhatsApp, en español",
    nota: "Se activa por edificio.",
    disponibleEn: ["plataforma", "backoffice"],
  },
});

// ─── Formato ───────────────────────────────────────────────────────────────

/**
 * Monto en pesos uruguayos: "$U 4.500" (entero, sin decimales), "$U 147,50"
 * (con centésimos, siempre 2) y "-$U 300". NaN, vacío o no numérico → "$U 0".
 * @param {number|string} n
 * @returns {string}
 */
export function fmtU(n) {
  const v = round2(numeroOpcional(n) || 0);
  const [entero, centesimos] = Math.abs(v).toFixed(2).split(".");
  const cuerpo = centesimos === "00" ? miles(entero) : `${miles(entero)},${centesimos}`;
  return `${v < 0 ? "-" : ""}$U ${cuerpo}`;
}

// ─── Cálculo ───────────────────────────────────────────────────────────────

/**
 * Cotización de un plan para la cartera de una administradora. Pura: no lee
 * estado. Todos los montos son por mes, + IVA, redondeados a centésimos.
 *
 * - precioUnidad vacío, NaN o negativo → el precio de lista del plan.
 * - totalFinal vacío, NaN o negativo → el total calculado (sin ajuste).
 * - El ajuste del total se reparte en el precio por unidad (precioUnidadFinal);
 *   los extras quedan con su monto. Si el total final queda por debajo de los
 *   extras, precioUnidadFinal da negativo (y debajoDelPiso, true).
 * - El total final es el que se tipeó: no se redondea para que cierre por
 *   unidad. Si precioUnidadFinal × unidades + extras no da justo ese total,
 *   precioUnidadExacto es false y el precio se presenta como promedio
 *   (rotuloPrecioUnidadBO).
 * - debajoDelPiso solo aplica con unidades > 0.
 *
 * @param {Object} params
 * @param {PlanKey} params.plan
 * @param {number|string} params.unidades — entero > 0 para que sea válida
 * @param {number|string} [params.edificios=0]
 * @param {number|string|null} [params.precioUnidad] — $U por unidad y por mes
 * @param {Object<string, { activo?: boolean, monto?: number|string }>} [params.extras]
 *   ej. { habiLlamada: { activo: true, monto: 3000 } }
 * @param {number|string|null} [params.totalFinal=null] — total ajustado a mano
 * @returns {CotizacionBO}
 */
export function cotizarBackoffice({ plan, unidades, edificios = 0, precioUnidad, extras = {}, totalFinal = null } = {}) {
  const planOk = PLAN_KEYS.includes(plan);
  const def = planOk ? PLANES_BO[plan] : null;
  const u = enteroNoNegativo(unidades);
  const precioLista = def ? def.precioDesde : 0;
  const pu = numeroOpcional(precioUnidad);
  const precio = pu !== null && pu >= 0 ? round2(pu) : precioLista;
  const subtotalUnidades = round2(u * precio);

  // Extras: en el orden de EXTRAS_BO, solo los disponibles en el plan, activos y con monto.
  const pedidos = extras && typeof extras === "object" ? extras : {};
  const extrasCotizados = Object.values(EXTRAS_BO)
    .filter((x) => planOk && x.disponibleEn.includes(plan))
    .map((x) => {
      const pedido = pedidos[x.key];
      const monto = pedido && pedido.activo ? round2(numeroOpcional(pedido.monto)) : 0;
      return { key: x.key, label: x.label, monto };
    })
    .filter((x) => x.monto > 0);
  const extrasTotal = round2(extrasCotizados.reduce((s, x) => s + x.monto, 0));
  const totalCalculado = round2(subtotalUnidades + extrasTotal);

  const tf = numeroOpcional(totalFinal);
  const total = tf !== null && tf >= 0 ? round2(tf) : totalCalculado;
  const ajusteMonto = round2(total - totalCalculado);
  const precioUnidadFinal = u > 0 ? round2((total - extrasTotal) / u) : 0;

  return {
    valido: planOk && u > 0,
    plan: planOk ? plan : null,
    planNombre: def ? def.nombre : "",
    unidades: u,
    edificios: enteroNoNegativo(edificios),
    precioLista,
    precioUnidad: precio,
    subtotalUnidades,
    extras: extrasCotizados,
    extrasTotal,
    totalCalculado,
    totalFinal: total,
    ajustado: Math.abs(total - totalCalculado) >= 0.005,
    ajusteMonto,
    ajustePct: totalCalculado ? round2((ajusteMonto / totalCalculado) * 100) : 0,
    precioUnidadFinal,
    precioUnidadExacto: cierraPorUnidad(u, precioUnidadFinal, extrasTotal, total),
    // Sin unidades no hay precio por unidad que comparar con la lista.
    debajoDelPiso: u > 0 && precioUnidadFinal < precioLista - 0.005,
  };
}

/**
 * Rótulo del precio por unidad final en la pantalla, el PDF y el resumen:
 * "Precio por unidad", o "Precio promedio por unidad" cuando el precio no
 * reproduce el total al centésimo (total ajustado a mano: 62.000 / 340 =
 * 182,35, que × 340 da 61.999). Así nadie lee 182,35 como precio exacto.
 * Con una cotización sin el flag (datos viejos) lo deduce de los montos.
 * @param {CotizacionBO|Object} cotizacion
 * @returns {string}
 */
export function rotuloPrecioUnidadBO(cotizacion) {
  const c = cotizacion || {};
  const exacto = typeof c.precioUnidadExacto === "boolean"
    ? c.precioUnidadExacto
    : cierraPorUnidad(
      enteroNoNegativo(c.unidades),
      numeroOpcional(c.precioUnidadFinal) || 0,
      numeroOpcional(c.extrasTotal) || 0,
      numeroOpcional(c.totalFinal) || 0
    );
  return exacto ? "Precio por unidad" : "Precio promedio por unidad";
}

// ─── Número y fechas de la propuesta ───────────────────────────────────────

/**
 * Número de propuesta "BO-AAAA-NNNN" (ej. "BO-2026-0427"). El año sale de la
 * fecha; la secuencia es 0–9999 y, si no viene, se deriva de la fecha
 * (getTime() % 10000).
 * @param {Date|string|number} [fecha=new Date()]
 * @param {number} [seq]
 * @returns {string}
 */
export function nroCotizacionBO(fecha = new Date(), seq) {
  const d = aFecha(fecha) || new Date();
  const s = numeroOpcional(seq);
  const n = s !== null ? Math.floor(Math.abs(s)) % 10000 : Math.abs(d.getTime()) % 10000;
  return `BO-${d.getFullYear()}-${String(n).padStart(4, "0")}`;
}

/**
 * Fecha larga: "13 de octubre de 2026". Fecha inválida o vacía → "".
 * @param {Date|string|number|{toDate: Function}} fecha
 * @returns {string}
 */
export function fechaLargaBO(fecha) {
  const d = aFecha(fecha);
  return d ? `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}` : "";
}

/**
 * Fecha corta: "13 oct 2026". Fecha inválida o vacía → "".
 * @param {Date|string|number|{toDate: Function}} fecha
 * @returns {string}
 */
export function fechaCortaBO(fecha) {
  const d = aFecha(fecha);
  return d ? `${d.getDate()} ${MESES_CORTOS[d.getMonth()]} ${d.getFullYear()}` : "";
}

/**
 * Vencimiento de la propuesta: fecha + días corridos (Date nuevo, no muta la
 * fecha recibida). Sin fecha válida cuenta desde hoy; días inválidos →
 * VALIDEZ_DIAS_DEFAULT.
 * @param {Date|string|number} fecha
 * @param {number} dias
 * @returns {Date}
 */
export function fechaVencimientoBO(fecha, dias) {
  const base = aFecha(fecha) || new Date();
  const d = new Date(base.getTime());
  d.setDate(d.getDate() + diasValidez(dias));
  return d;
}

// ─── Resumen, guardado y recarga ───────────────────────────────────────────

/**
 * Resumen para WhatsApp (con *negritas*): plan, cartera, precio por unidad
 * (promedio si no cierra con el total), extras, total mensual, condiciones y
 * validez. Todo "+ IVA", sin montos con IVA.
 * @param {Object} params
 * @param {CotizacionBO} params.cotizacion
 * @param {string} [params.administradoraNombre]
 * @param {string} [params.nro]
 * @param {Date|string} [params.validezHasta]
 * @returns {string}
 */
export function resumenWhatsappBO({ cotizacion, administradoraNombre, nro, validezHasta } = {}) {
  const c = cotizacion || cotizarBackoffice();
  const nombre = String(administradoraNombre || "").trim();
  const lineas = [nombre ? `*Propuesta Habitia para ${nombre}*` : "*Propuesta Habitia*"];
  if (nro) lineas.push(`Nº ${nro}`);
  lineas.push("");
  if (c.planNombre) lineas.push(`Plan: *${c.planNombre}*`);
  lineas.push(`Cartera: ${carteraTexto(enteroNoNegativo(c.edificios), enteroNoNegativo(c.unidades))}`);
  lineas.push(`${rotuloPrecioUnidadBO(c)}: ${fmtU(c.precioUnidadFinal)} por mes ${IVA_LEYENDA}`);
  (c.extras || []).forEach((x) => {
    lineas.push(`${x.label}: ${fmtU(x.monto)} por mes ${IVA_LEYENDA}`);
  });
  lineas.push(`*Total mensual: ${fmtU(c.totalFinal)} ${IVA_LEYENDA}*`);
  lineas.push("");
  lineas.push("Sin mínimos · sin permanencia · migración sin costo");
  const vence = fechaLargaBO(validezHasta);
  if (vence) lineas.push(`Válida hasta el ${vence}`);
  return lineas.join("\n");
}

/**
 * Entrada que se guarda en la ficha de la administradora
 * (`administraciones/{id}`: cotizacionBackoffice y cotizacionesBackoffice).
 * Objeto plano para Firestore: sin undefined, fechas en ISO y montos a
 * centésimos. Sin nro genera uno; sin validezHasta la calcula con validezDias.
 * @param {Object} params
 * @param {CotizacionBO} params.cotizacion
 * @param {string} [params.nro]
 * @param {Date|string} [params.fecha] — momento de la cotización (default: ahora)
 * @param {number} [params.validezDias]
 * @param {Date|string} [params.validezHasta]
 * @param {boolean} [params.mostrarListaTachada]
 * @param {string} [params.por] — email de quien cotiza
 * @returns {{ nro: string, at: string, por: string, plan: PlanKey|null, planNombre: string,
 *   edificios: number, unidades: number, precioLista: number, precioUnidad: number,
 *   extras: ExtraCotizado[], extrasTotal: number, totalCalculado: number, totalFinal: number,
 *   ajustado: boolean, ajusteMonto: number, precioUnidadFinal: number, validezDias: number,
 *   validezHasta: string, mostrarListaTachada: boolean }}
 */
export function payloadCotizacionBO({ cotizacion, nro, fecha, validezDias, validezHasta, mostrarListaTachada, por } = {}) {
  const c = cotizacion || {};
  const at = aFecha(fecha) || new Date();
  const dias = diasValidez(validezDias);
  const vence = aFecha(validezHasta) || fechaVencimientoBO(at, dias);
  const plan = PLAN_KEYS.includes(c.plan) ? c.plan : null;
  return {
    nro: String(nro || nroCotizacionBO(at)),
    at: at.toISOString(),
    por: String(por || ""),
    plan,
    planNombre: String(c.planNombre || (plan ? PLANES_BO[plan].nombre : "")),
    edificios: enteroNoNegativo(c.edificios),
    unidades: enteroNoNegativo(c.unidades),
    precioLista: round2(c.precioLista),
    precioUnidad: round2(c.precioUnidad),
    extras: (Array.isArray(c.extras) ? c.extras : []).filter(Boolean).map((x) => ({
      key: String(x.key || ""),
      label: String(x.label || ""),
      monto: round2(x.monto),
    })),
    extrasTotal: round2(c.extrasTotal),
    totalCalculado: round2(c.totalCalculado),
    totalFinal: round2(c.totalFinal),
    ajustado: !!c.ajustado,
    ajusteMonto: round2(c.ajusteMonto),
    precioUnidadFinal: round2(c.precioUnidadFinal),
    validezDias: dias,
    validezHasta: vence.toISOString(),
    mostrarListaTachada: !!mostrarListaTachada,
  };
}

/**
 * Inputs del cotizador a partir de una cotización guardada ("Usar estos
 * datos"). totalFinal es null si la guardada no estaba ajustada, así el total
 * vuelve a seguir al calculado. Acepta los extras como array guardado
 * ([{ key, monto }]) o como mapa de inputs ({ habiLlamada: { activo, monto } }).
 * @param {Object|null} entrada — salida de payloadCotizacionBO
 * @returns {{ plan: PlanKey, unidades: number, edificios: number, precioUnidad: number,
 *   extras: Object<string, { activo: boolean, monto: number }>, totalFinal: number|null,
 *   validezDias: number, mostrarListaTachada: boolean }}
 */
export function inputsDesdeGuardadaBO(entrada) {
  const e = entrada && typeof entrada === "object" ? entrada : {};
  const plan = PLAN_KEYS.includes(e.plan) ? e.plan : PLAN_DEFAULT;
  const pu = numeroOpcional(e.precioUnidad);

  const lista = Array.isArray(e.extras) ? e.extras : null;
  const mapa = !lista && e.extras && typeof e.extras === "object" ? e.extras : {};
  const extras = {};
  Object.values(EXTRAS_BO).forEach((x) => {
    const guardado = lista ? lista.find((it) => it && it.key === x.key) : mapa[x.key];
    const monto = guardado ? round2(numeroOpcional(guardado.monto)) : 0;
    extras[x.key] = {
      // En el array guardado solo figuran los extras que estaban activos.
      activo: lista ? !!guardado : !!(guardado && guardado.activo),
      monto: monto > 0 ? monto : 0,
    };
  });

  const tf = numeroOpcional(e.totalFinal);
  const tc = numeroOpcional(e.totalCalculado);
  const ajustado = typeof e.ajustado === "boolean"
    ? e.ajustado
    : tf !== null && tc !== null && Math.abs(tf - tc) >= 0.005;

  return {
    plan,
    unidades: enteroNoNegativo(e.unidades),
    edificios: enteroNoNegativo(e.edificios),
    precioUnidad: pu !== null && pu >= 0 ? round2(pu) : PLANES_BO[plan].precioDesde,
    extras,
    totalFinal: ajustado && tf !== null && tf >= 0 ? round2(tf) : null,
    validezDias: diasValidez(e.validezDias),
    mostrarListaTachada: !!e.mostrarListaTachada,
  };
}

/**
 * Primer entero de un texto libre (`nEdificios` de la ficha CRM es texto de
 * referencia): "~40 edificios" → 40, "1.200" → 1200. Vacío o sin números → 0.
 * @param {string|number|null} txt
 * @returns {number}
 */
export function edificiosDesdeTexto(txt) {
  if (txt === null || txt === undefined) return 0;
  const m = String(txt).match(/\d+(?:\.\d{3}(?!\d))*/);
  return m ? parseInt(m[0].replace(/\./g, ""), 10) : 0;
}
